import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Collapse, IconButton, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { EVENT_APP_CATEGORIES } from '../../types/event';
import { EventFilters, DEFAULT_FILTERS, isAnyFilterActive } from '../../utils/eventFiltering';
import { buildDenSelectorModel } from '../../utils/denOptions';

interface EventFilterBarProps {
  filters: EventFilters;
  dens: string[];
  onChange: (filters: EventFilters) => void;
}

const toggle = (arr: string[], value: string): string[] =>
  arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

const ALL_LOCATIONS_VALUE = '';

const EventFilterBar: React.FC<EventFilterBarProps> = ({ filters, dens, onChange }) => {
  const [searchOpen, setSearchOpen] = useState(filters.search !== '');
  const denModel = useMemo(() => buildDenSelectorModel(dens), [dens]);

  // Option value (its label) -> the raw event.location strings it filters by.
  const matchValuesByValue = useMemo(() => {
    const map = new Map<string, string[]>();
    map.set(ALL_LOCATIONS_VALUE, []);
    [...denModel.dens, ...denModel.outings].forEach((opt) => {
      map.set(opt.label, opt.matchValues);
    });
    return map;
  }, [denModel]);

  // Which option (if any) the current filters.dens reflects.
  const selectedValue = useMemo(() => {
    const match = [...denModel.dens, ...denModel.outings].find(
      (opt) =>
        opt.matchValues.length === filters.dens.length &&
        opt.matchValues.every((v) => filters.dens.includes(v))
    );
    return match ? match.label : ALL_LOCATIONS_VALUE;
  }, [denModel, filters.dens]);

  const handleLocationChange = (value: string): void => {
    onChange({ ...filters, dens: matchValuesByValue.get(value) ?? [] });
  };

  const hasDenSection = denModel.dens.length > 0 || denModel.outings.length > 0;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Search (collapsed behind icon) + Clear all */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton aria-label="Search events" onClick={() => setSearchOpen((o) => !o)}>
          <SearchIcon />
        </IconButton>
        <Collapse in={searchOpen} orientation="horizontal">
          <TextField
            placeholder="Search events..."
            size="small"
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            sx={{ minWidth: 240 }}
          />
        </Collapse>
        {isAnyFilterActive(filters) && (
          <Button size="small" sx={{ ml: 'auto' }} onClick={() => onChange(DEFAULT_FILTERS)}>
            Clear all
          </Button>
        )}
      </Box>

      {/* Category */}
      <Typography variant="overline" color="text.secondary">
        Category
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        {EVENT_APP_CATEGORIES.map((cat) => {
          const selected = filters.categories.includes(cat);
          return (
            <Chip
              key={cat}
              label={cat}
              clickable
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              onClick={() => onChange({ ...filters, categories: toggle(filters.categories, cat) })}
            />
          );
        })}
      </Box>

      {/* Location (native OS picker) */}
      {hasDenSection && (
        <TextField
          select
          fullWidth
          label="Location"
          size="small"
          value={selectedValue}
          onChange={(e) => handleLocationChange(e.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          data-testid="location-select"
          sx={{ mb: 2, maxWidth: 360 }}
        >
          <option value={ALL_LOCATIONS_VALUE}>All locations</option>
          {denModel.dens.map((opt) => (
            <option key={opt.label} value={opt.label}>
              {opt.label}
            </option>
          ))}
          {denModel.outings.length > 0 && (
            <optgroup label="Outings">
              {denModel.outings.map((opt) => (
                <option key={opt.label} value={opt.label}>
                  {opt.label}
                </option>
              ))}
            </optgroup>
          )}
        </TextField>
      )}

      {/* Availability */}
      <Typography variant="overline" color="text.secondary">
        Availability
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label="Has spots left"
          clickable
          color={filters.hasSpotsOnly ? 'primary' : 'default'}
          variant={filters.hasSpotsOnly ? 'filled' : 'outlined'}
          onClick={() => onChange({ ...filters, hasSpotsOnly: !filters.hasSpotsOnly })}
        />
      </Box>
    </Box>
  );
};

export default EventFilterBar;
